<template>
    <BaseModal :title="$t('title')" v-on="$listeners">
        <BaseCard padded class="l-stack l-gap-2">
            <div>
                <p class="form-label">
                    {{ $t('name') }}
                </p>

                <BaseInput v-model="name" :placeholder="$t('name')" />
            </div>

            <div>
                <p class="form-label">
                    {{ $t('type') }}
                </p>

                <BaseMultiselect
                    v-model="trailerKind"
                    :options="trailerKindOptions"
                    :custom-label="
                        option => $t(`shared.lsva.trailerTypes.${option}`)
                    "
                    :placeholder="$t('type')"
                />
            </div>

            <div>
                <p class="form-label">
                    {{ $t('trailerAssetType') }}
                </p>

                <BaseMultiselect
                    v-model="trailerAssetType"
                    :options="trailerAssetTypeOptions"
                    track-by="id"
                    label="name"
                    :placeholder="$t('trailerAssetType')"
                />
            </div>

            <div>
                <p class="form-label l-inline l-gap-0 l-center-v">
                    <span>{{ $t('weight') }}</span>
                    <InfoIcon
                        v-tooltip="$t('weightHint')"
                        width="1em"
                        height="1em"
                    />
                </p>

                <BaseInput
                    v-model.number="weight"
                    class="l-full-width"
                    type="number"
                    step="any"
                    :placeholder="$t('weight')"
                >
                    <template #postfix>
                        t
                    </template>
                </BaseInput>
            </div>
        </BaseCard>

        <template #footer>
            <div class="l-inline l-justify-end">
                <BaseButton
                    size="small"
                    :disabled="isDisabled"
                    :is-loading="isLoading"
                    @click="handleSubmit"
                >
                    {{ $t('submit') }}
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import { mapState } from 'vuex'

import { httpHelper } from '@/utils'
import BaseButton from '../redesigned/BaseButton'
import BaseCard from '../redesigned/BaseCard'
import BaseInput from '../redesigned/BaseInput'
import BaseModal from '../redesigned/BaseModal'
import BaseMultiselect from '../redesigned/BaseMultiselect'
import InfoIcon from '../icons/InfoIcon'

export default {
    name: 'LsvaTrailerEditModal',
    components: {
        BaseButton,
        BaseCard,
        BaseInput,
        BaseModal,
        BaseMultiselect,
        InfoIcon,
    },
    props: {
        onSubmit: {
            type: Function,
            default: null,
        },
        trailer: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            name: '',
            trailerAssetType: null,
            trailerKind: null,
            trailerKindOptions: ['S', 'T'],
            weight: null,
        }
    },
    computed: {
        ...mapState('tracker', ['assetTypes']),
        trailerAssetTypeOptions() {
            return this.assetTypes.filter(({ type }) => type === 'trailer')
        },
        isDisabled() {
            return !this.name || !this.trailerAssetType || !this.trailerKind
        },
    },
    watch: {
        trailer: {
            immediate: true,
            handler() {
                this.name = this.trailer?.name
                this.trailerAssetType = this.trailerAssetTypeOptions.find(
                    opt => opt.id === this.trailer?.asset_type
                )
                this.trailerKind = this.trailer?.type
                this.weight = this.trailer?.weight
                    ? this.trailer.weight / 1000
                    : this.trailer.weight
            },
        },
    },
    methods: {
        async handleSubmit() {
            this.isLoading = true
            await httpHelper.put(`/lsva/trailers/${this.trailer.id}/`, {
                name: this.name,
                asset_type: this.trailerAssetType.id,
                type: this.trailerKind,
                weight: (this.weight || 0) * 1000,
            })
            this.onSubmit?.()
            this.$emit('close')
            this.$notify({
                title: this.$t('shared.success'),
                text: this.$t('weightHint'),
            })
        },
    },
}
</script>

<i18n>
{
    "en": {
        "name": "Name",
        "submit": "Apply",
        "title": "Edit trailer",
        "trailerAssetType": "Group",
        "type": "Type",
        "weight": "Weight",
        "weightHint": "The change will not affect past trips"
    },
    "de": {
        "name": "Name",
        "submit": "Anwenden",
        "title": "Anhänger bearbeiten",
        "trailerAssetType": "Gruppe",
        "type": "Typ",
        "weight": "Gewicht",
        "weightHint": "Die Änderung wirkt sich nicht auf vergangene Fahrten aus"
    },
    "fr": {
        "name": "Nom",
        "submit": "Appliquer",
        "title": "Modifier la remorque",
        "trailerAssetType": "Groupe",
        "type": "Type",
        "weight": "Poids",
        "weightHint": "Le changement n’affectera pas les trajets passés"
    },
    "it": {
        "name": "Nome",
        "submit": "Applicare",
        "title": "Modifica il rimorchio",
        "trailerAssetType": "Gruppo",
        "type": "Typo",
        "weight": "Peso",
        "weightHint": "La modifica non influirà sui viaggi passati"
    }
}
</i18n>
